import * as Utils from '@js/modules/utils';

import MapInterface from '@js/modules/maps/mapInterface';


const RideTrackMapModule = {
    _drawLineModel: {
        isInteractive: true,

        // activity points
        linePoints: [],

        // Minimum zoom to show direction arrows
        minZoomDirectionArrow: 13,
        // Direction arrows for the main track
        directionArrows: [],

        // distance for callback functions (in km)
        distance: 0,
        // Distance updated
        distanceUpdated: undefined,

        // Prevent double click on marker and on map at the same time
        markerEventClicked: false
    },

    plugDrawLine(params = {}) {
        if (Utils.isPresent(params.isInteractive)) {
            this._drawLineModel.isInteractive = params.isInteractive;
        }

        this.addMapTrackLine(this._model.mapTrackLineName);

        this._onMapClick();

        this._onDrawLineZoomChange();

        this._mapInitialSetup(params.initialPoints);
    },

    drawLine(linePoints, {formatPoints = false} = {}) {
        if (Utils.isEmpty(linePoints)) {
            return;
        }

        this.addMapTrackLine(this._model.mapTrackLineName);

        this.clearDrawLine();

        if (formatPoints) {
            if (Array.isArray(linePoints)) {
                for (let pointIndex = 0; pointIndex < linePoints.length; pointIndex++) {
                    this._drawLineModel.linePoints.push(new MapInterface.PointInfo(linePoints[pointIndex], null, {computed: linePoints.length - 1 !== pointIndex}));
                }
            } else {
                // Error: just display a direct line
                this._drawLineModel.linePoints.push(new MapInterface.PointInfo(linePoints, null, {computed: false}));
            }
        } else {
            this._drawLineModel.linePoints = linePoints;
        }

        this._redrawKeyPoints();
    },

    onDistanceUpdated(callback) {
        this._drawLineModel.distanceUpdated = callback;
    },

    extendDrawLineBounds(bounds) {
        if (this._drawLineModel.linePoints.length > 0) {
            this._drawLineModel.linePoints.forEach((point) => {
                bounds = bounds.extend([point.latLng.lng, point.latLng.lat]);
            });
        }

        return bounds;
    },

    // True once an actual line is drawn (more than the single start point). Reads the canonical
    // interpolated curve (_mapModel.linePoints) — the same array generateRoundTrip/elevation use.
    hasDrawnRoute() {
        return (this._mapModel?.linePoints?.length || 0) > 1;
    },

    serializePoints() {
        // output format:
        let serializeString = '';

        for (let i = 0; i < this._drawLineModel.linePoints.length; i++) {
            const currentLinePoint = this._drawLineModel.linePoints[i];
            let deltaDistance = 0;
            // const deltaTime = currentLinePoint.deltaTime;
            let prevLinePoint;
            if (i !== 0) {
                prevLinePoint = this._drawLineModel.linePoints[i - 1];
                if (prevLinePoint.deltaPause === 0) {
                    deltaDistance = currentLinePoint.deltaDistance || this.distHaversine(this._drawLineModel.linePoints[i - 1].latLng, currentLinePoint.latLng, MapInterface.COORDINATES_PRECISION);
                }
            }

            let pointInfoType;
            if (i === 0) {
                pointInfoType = 'start';
            } else if (i === this._drawLineModel.linePoints.length - 1) {
                pointInfoType = 'end';
            } else if (currentLinePoint.computed) {
                pointInfoType = 'auto';
            } else {
                pointInfoType = 'manual';
            }

            serializeString += [
                pointInfoType,
                parseFloat(currentLinePoint.latLng.lat.toFixed(MapInterface.COORDINATES_PRECISION)),
                parseFloat(currentLinePoint.latLng.lng.toFixed(MapInterface.COORDINATES_PRECISION)),
                currentLinePoint.elevation,
                deltaDistance
            ].join(',') + ';';
        }

        return serializeString;
    },

    clearDrawLine({clearFollowedRide = false} = {}) {
        // kill all the things
        this._clearKeyPoints({clearAll: true});

        this._drawLineModel.distance = 0;

        this._drawLineModel.directionArrows = [];
        // this._drawLineModel.activityPolylines = [];
        this._drawLineModel.linePoints = [];

        this._model.pointsByDistanceTraveled = {};

        if (!clearFollowedRide && this._drawLineModel.distanceUpdated) {
            this._drawLineModel.distanceUpdated(this._drawLineModel.distance);
        }
    },

    /* Private Methods
    ******************** */
    _onMapClick() {
        // Add a listener for the click event
        this._model.map.on('click', Utils.debounce((event) => {
            if (!this._drawLineModel.isInteractive) {
                return;
            }

            if (this._model.isDoubleClick) {
                this._model.isDoubleClick = false;
                return;
            }

            if (this._mapModel) {
                this._mapModel.isShowingRide = false;
            }

            // Avoid double click
            this._addKeyPointAtClickLoc(event.lngLat);
        }, 200));

        this._model.map.on('dblclick', () => this._model.isDoubleClick = true);

        // Manage clicks on map: start marker is current position
        // Don't add point if double click or double tapping
        this._model.map.doubleClickZoom.enable();
    },

    _onDrawLineZoomChange() {
        this._model.map.on('zoomend', () => {
            if (!this.isDataChangedOnZoom(this._model.map.getZoom(), this._model.previousZoom)) {
                return;
            }

            this._redrawKeyPoints({refresh: true});
            this._drawDirectionArrows();

            // Useless
            // if (this._model.map.getZoom() < window.settings.min_zoom_polyline - 1) {
            //     this._clearMarkersPoints();
            // }
        });
    },

    _mapInitialSetup(initialPoints) {
        if (Utils.isEmpty(initialPoints)) {
            return;
        }

        // establish key points to edit with
        this._setupPoints(initialPoints);

        // if we're in edit mode, setup key points with events
        this._redrawKeyPoints();

        // draw trip polyline, add to map
        this._redrawTripLine();
    },

    _addKeyPointAtClickLoc(coordinates) {
        // Prevent bubbling click on marker and on map
        if (this._drawLineModel.markerEventClicked) {
            this._drawLineModel.markerEventClicked = false;
            return;
        }

        if (this._drawLineModel.linePoints.length === 0) {
            this.clear();
        }

        // ensure that we aren't in the process of dragging a point or computing a path
        if (!this._model.isComputingRoad) {
            // create new point, add to map, redraw the trip line

            // we don't want to use the routing api if it's only the first point on the map (there's no beginning / end)
            let firstPoint = false;
            if (this._drawLineModel.linePoints.length === 0) {
                firstPoint = true;
            }

            if (this._model.computingRoadCallback) {
                this._model.computingRoadCallback(true, {firstPoint: firstPoint});
            }

            if (firstPoint) {
                // Use geotracking as first point if present (in explore mode)
                if (this._geolocationModel) {
                    const locationPoint = this.getCurrentLocationPoint();

                    if (locationPoint) {
                        this._drawLineModel.linePoints.push(new MapInterface.PointInfo({
                            lat: locationPoint.latitude,
                            lng: locationPoint.longitude
                        }, null, {computed: false}));

                        firstPoint = false;
                    }
                }
            }

            // need to remember where in the linePoints array we're adding to, so we can prep the undo function
            // const prevEndingIndex = this._drawLineModel.linePoints.length;

            // create any points involved, add to linePoints array
            if (this._model.snapToRoads && !firstPoint) {
                this.getSnapToRoadsRoute(
                    this._drawLineModel.linePoints[this._drawLineModel.linePoints.length - 1].latLng,
                    coordinates,
                    null,
                    (pointArray) => {
                        if (Array.isArray(pointArray)) {
                            for (let pointIndex = 0; pointIndex < pointArray.length; pointIndex++) {
                                this._drawLineModel.linePoints.push(new MapInterface.PointInfo(pointArray[pointIndex], null, {computed: pointArray.length - 1 !== pointIndex}));
                            }
                        } else {
                            // Error: just display a direct line
                            this._drawLineModel.linePoints.push(new MapInterface.PointInfo(coordinates, null, {computed: false}));
                        }

                        this._redrawKeyPoints();

                        if (this._model.computingRoadCallback) {
                            this._model.computingRoadCallback(false);
                        }

                        if (this.hasModule('RIDE_STORE') && this.storeRide) {
                            this.storeRide(this._drawLineModel.linePoints);
                        }
                    }
                );
            } else {
                this._drawLineModel.linePoints.push(new MapInterface.PointInfo(coordinates, null, {computed: false}));
                this._redrawKeyPoints();

                if (this._model.computingRoadCallback) {
                    this._model.computingRoadCallback(false);
                }

                if (this.hasModule('RIDE_STORE') && this.storeRide) {
                    this.storeRide(this._drawLineModel.linePoints);
                }
            }
        }
    },

    _setupPoints(initialPoints) {
        this._drawLineModel.linePoints = [];

        // Draw polylines points
        if (initialPoints && initialPoints.length > 0) {
            for (let i = 0; i < initialPoints.length; i++) {
                this._drawLineModel.linePoints.push(new MapInterface.PointInfo(
                    {
                        lat: initialPoints[i].latitude,
                        lng: initialPoints[i].longitude
                    },
                    null, // keyPoint
                    {
                        computed: Utils.isPresent(initialPoints[i].computed), // i !== 0 || i !== initialPoints.length - 1,
                        deltaDistance: Utils.isEmpty(initialPoints[i].deltaDistance) ? 0 : initialPoints[i].deltaDistance,
                        deltaTime: Utils.isEmpty(initialPoints[i].deltaTime) ? 0 : initialPoints[i].deltaTime,
                        deltaPause: Utils.isEmpty(initialPoints[i].deltaPause) ? 0 : initialPoints[i].deltaPause,
                        datetime: initialPoints[i].datetime,
                        elevation: initialPoints[i].elevation,
                        speed: initialPoints[i].speed,
                        heartRate: initialPoints[i].heartRate
                    }
                ));
            }
        }
    },

    _clearKeyPoints({
                        keepStartStop = false,
                        clearAll = false
                    } = {}) {
        for (let linePointIndex = 0; linePointIndex < this._drawLineModel.linePoints.length; linePointIndex++) {
            if (keepStartStop) {
                if (linePointIndex === 0 || linePointIndex === this._drawLineModel.linePoints.length - 1) {
                    continue;
                }
            }

            const thisLinePoint = this._drawLineModel.linePoints[linePointIndex];
            if (thisLinePoint && (thisLinePoint.keyPoint !== null || thisLinePoint.keyPoint !== undefined)) {
                thisLinePoint.keyPoint = null;
            }
        }

        if (clearAll) {
            this.resetMapSource('arrow-marker');
            this.resetMapSource('ghost-marker');
            this.resetMapSource('step-marker');
            this.resetMapSource('start-marker');
            this.resetMapSource('finish-marker');

            this.resetMapSource('running-primary-marker');
            this.resetMapSource('hiking-primary-marker');
            this.resetMapSource('bike-primary-marker');
            this.resetMapSource('walk-primary-marker');
            this.resetMapSource('cluster-marker');

            this.resetMapSource(this._model.mapTrackLineName);
        }
    },

    _redrawTripLine: function () {
        // clear pointsByDistanceTraveled object (used by highCharts)
        this._model.pointsByDistanceTraveled = {};

        // set initial 'total Distance' variable
        let totalDistance = 0;

        // remove all activity line polylines from the map
        this.resetMapSource(this._model.mapTrackLineName);

        // // clear both arrays for re-use
        // this._drawLineModel.activityPolylines = [];

        // distance variables for lines
        let lastLineStartPointIndex = 0;

        // what distance marker we are currently on
        for (let linePointIndex = 0; linePointIndex < this._drawLineModel.linePoints.length; linePointIndex++) {
            let thisLegDist;

            // if we have reached a pause point OR the last point (finish) draw this segment and process
            if (linePointIndex === this._drawLineModel.linePoints.length - 1 /* || this._drawLineModel.linePoints[linePointIndex + 1].deltaPause > 0 */) {
                // draw this line segment
                const nextLine = [];
                for (let j = lastLineStartPointIndex; j <= linePointIndex; j++) {
                    const thisLinePointLatLng = this._drawLineModel.linePoints[j].latLng;
                    let prevLinePointLatLng;

                    nextLine.push(this._drawLineModel.linePoints[j].latLng);

                    if (j !== lastLineStartPointIndex) {
                        prevLinePointLatLng = this._drawLineModel.linePoints[j - 1].latLng;

                        if (this._drawLineModel.linePoints[linePointIndex].deltaDistance) {
                            thisLegDist = this._drawLineModel.linePoints[j].deltaDistance;
                            totalDistance += thisLegDist;
                        } else {
                            thisLegDist = Utils.flooredNum(this.distHaversine(prevLinePointLatLng, thisLinePointLatLng), MapInterface.COORDINATES_PRECISION);
                            totalDistance += thisLegDist;
                        }
                    }

                    // handle the total distance stamp for Highcharts point mapping
                    let traveledDistance = totalDistance;
                    if (this._model.distanceUnits === 'mi') {
                        traveledDistance /= 1.609344;
                    }

                    this._model.pointsByDistanceTraveled[Math.floor(traveledDistance)] = thisLinePointLatLng;
                }

                // draw lines on map and thumbnails in the set color
                const thisLine = {
                    type: 'Feature',
                    geometry: {
                        type: 'LineString',
                        coordinates: nextLine.map((lngLat) => [lngLat.lng, lngLat.lat])
                    }
                };

                this.updateSource(this._model.mapTrackLineName, thisLine);

                // this._drawLineModel.activityPolylines.push(thisLine.geometry.coordinates);

                // kick last line start point index (start at the next point, the resume)
                lastLineStartPointIndex = linePointIndex + 1;
            }
        }

        // distance to km
        this._drawLineModel.distance = Utils.flooredNum(totalDistance, MapInterface.COORDINATES_PRECISION) / 1000;

        // run distance updated function stack
        if (this._drawLineModel.distanceUpdated) {
            this._drawLineModel.distanceUpdated(this._drawLineModel.distance);
        }
    },

    _redrawKeyPoints({refresh = false} = {}) {
        // clear key points
        this._clearKeyPoints();

        let thisLinePoint;
        let markerName;
        const mapMarkers = {};
        const stepMarkerInterval = this.getZoomDictionary(this._model.map.getZoom()).stepDist;

        // loop through, if the point should be a keypoint, set the keypoint variable and add to map
        let distanceSinceLastKeyPoint = 0;
        let distanceWithEndKeyPoint;
        for (let linePointIndex = 0; linePointIndex < this._drawLineModel.linePoints.length; linePointIndex++) {
            thisLinePoint = this._drawLineModel.linePoints[linePointIndex];

            if (linePointIndex === 0) {
                if (refresh) {
                    continue;
                }

                // set start point options
                markerName = 'start-marker';
            } else if (linePointIndex === this._drawLineModel.linePoints.length - 1) {
                if (refresh) {
                    continue;
                }

                // set finish point options
                markerName = 'finish-marker';
            } else if (thisLinePoint.computed) {
                // if point is computed, display points accordingly to the current zoom
                const prevLineComputedPoint = this._drawLineModel.linePoints[linePointIndex - 1];
                distanceSinceLastKeyPoint += this.distHaversine(prevLineComputedPoint.latLng, thisLinePoint.latLng, MapInterface.COORDINATES_PRECISION);
                distanceWithEndKeyPoint = this.distHaversine(thisLinePoint.latLng, this._drawLineModel.linePoints[this._drawLineModel.linePoints.length - 1].latLng, MapInterface.COORDINATES_PRECISION);

                // if more than X meters have passed since last key point, make a normal key point.
                // otherwise, simply kick the distance since last key point variable
                if (stepMarkerInterval && distanceSinceLastKeyPoint > stepMarkerInterval && distanceWithEndKeyPoint > 100) {
                    markerName = 'step-marker';
                    distanceSinceLastKeyPoint = 0;
                } else {
                    markerName = null;
                }
            } else {
                // normal point need to be present when dragging the siblings points
                markerName = 'step-marker';
            }

            if (markerName && thisLinePoint.latLng) {
                thisLinePoint.keyPoint = {
                    type: 'Feature',
                    properties: {
                        markerId: markerName,
                        linePointIndex: linePointIndex,
                        draggable: false
                    },
                    geometry: {
                        type: 'Point',
                        coordinates: [thisLinePoint.latLng.lng, thisLinePoint.latLng.lat]
                    }
                };
                mapMarkers[markerName] = (mapMarkers[markerName] || []).concat(thisLinePoint.keyPoint);
            }
        }

        Object.keys(mapMarkers)
            .forEach((markerType) => {
                this.updateSource(markerType, {
                    type: 'FeatureCollection',
                    features: mapMarkers[markerType]
                });
            });

        if (this._drawLineModel.linePoints.length > 1) {
            this._redrawTripLine();
        }
    },

    _drawDirectionArrows() {
        this._removeDirectionArrows();

        if (this._model.map.getZoom() < this._drawLineModel.minZoomDirectionArrow) {
            return;
        }

        if (this._drawLineModel.linePoints.length < 3) {
            return;
        }

        // how often SHOULD we be placing direction arrows?
        const directionArrowInterval = this.getZoomDictionary(this._model.map.getZoom()).directionArrow;
        if (!directionArrowInterval) {
            return;
        }

        // draw a fake point in between every key point to allow user to insert a new point here.
        // variable for distance since last direction arrow
        let distanceSinceLastDirectionArrow = 0;

        // after placing a direction arrow, how much distance is left over (so we can place correctly after the next invisible point)
        let carryoverDistance = 0;
        let lastKeyPointIndex = 0;
        for (let linePointIndex = 0; linePointIndex < this._drawLineModel.linePoints.length; linePointIndex++) {
            let prevLinePoint;
            const thisLinePoint = this._drawLineModel.linePoints[linePointIndex];

            // variable for point-to-point distance
            let distanceBetween = 0;
            if (linePointIndex > 0) {
                prevLinePoint = this._drawLineModel.linePoints[linePointIndex - 1];
                distanceBetween = this.distHaversine(prevLinePoint.latLng, thisLinePoint.latLng, MapInterface.COORDINATES_PRECISION);
                distanceSinceLastDirectionArrow += distanceBetween;
            }

            // if we've traveled more than the direction arrow interval, find out how many to place
            let directionArrowsToPlace = 0;
            if (distanceSinceLastDirectionArrow > directionArrowInterval) {
                directionArrowsToPlace = Math.floor(distanceSinceLastDirectionArrow / directionArrowInterval);
            }

            // start with carryover distance
            // move from point A to point B, placing direction arrows at interval specified (remove carryover from last loop iteration)
            // when done, set carryover distance and continue
            if (directionArrowsToPlace > 0) {
                for (let directionArrowIndex = 1; directionArrowIndex <= directionArrowsToPlace; directionArrowIndex++) {
                    const distanceToNextDirectionArrow = (directionArrowInterval * directionArrowIndex) - carryoverDistance;
                    const percentageBetweenPoints = distanceToNextDirectionArrow / distanceBetween;

                    // we now know at what percentage distance the direction arrow should be placed. Use to put it on the line
                    const directionArrowLat = prevLinePoint.latLng.lat + ((thisLinePoint.latLng.lat - prevLinePoint.latLng.lat) * percentageBetweenPoints);
                    const directionArrowLng = prevLinePoint.latLng.lng + ((thisLinePoint.latLng.lng - prevLinePoint.latLng.lng) * percentageBetweenPoints);

                    const diffLat = thisLinePoint.latLng.lat - prevLinePoint.latLng.lat;
                    const diffLng = thisLinePoint.latLng.lng - prevLinePoint.latLng.lng;
                    // const center = [thisLinePoint.latLng.lat + diffLat / 2, thisLinePoint.latLng.lng + diffLng / 2];
                    const angle = (360 - ((Math.atan2(diffLat, diffLng) * 57.295779513082) % 360));

                    // create direction arrow object
                    const newDirectionArrow = new MapInterface.DirectionArrow(
                        {
                            type: 'Feature',
                            properties: {
                                angle: angle,
                                linePointIndex: linePointIndex,
                                directionArrowIndex: directionArrowIndex
                            },
                            geometry: {
                                type: 'Point',
                                coordinates: [directionArrowLng, directionArrowLat]
                            }
                        },
                        lastKeyPointIndex
                    );

                    // add to array for reference
                    this._drawLineModel.directionArrows.push(newDirectionArrow);

                    if (directionArrowIndex === directionArrowsToPlace) {
                        // last need
                        carryoverDistance = distanceBetween - (percentageBetweenPoints * distanceBetween);
                        distanceSinceLastDirectionArrow = carryoverDistance;
                    }
                }
            } else {
                carryoverDistance += distanceBetween;
            }

            if (thisLinePoint.keyPoint !== null) {
                lastKeyPointIndex = linePointIndex;
            }
        }

        this.updateSource('arrow-marker', {
            type: 'FeatureCollection',
            features: this._drawLineModel.directionArrows.map(function (directionArrow) {
                return directionArrow.keyPoint;
            })
        });
    },

    _removeDirectionArrows() {
        if (!this._drawLineModel.directionArrows.length) {
            return;
        }

        this.resetMapSource('arrow-marker');

        this._drawLineModel.directionArrows = [];
    }
};

export default RideTrackMapModule;
